import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";
import { getToken, getUserId } from "../services/authorize";
import ThaiDateInput from '../components/ThaiDateInput'
import { toISODate } from '../utils/thaiDate'

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return toISODate(d);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const statusLabel = {
  active: "กำลังยืม",
  overdue: "เกินกำหนด",
  returned: "คืนแล้ว",
  lost: "สูญหาย"
};

export default function LibraryRenewals() {
  const [loans, setLoans] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    loan_no: "",
    new_due_date: "",
    renew_note: ""
  });

  const headers = useMemo(() => ({ authorization: `Bearer ${getToken()}` }), []);

  const renewableLoans = useMemo(
    () => loans.filter((loan) => ["active", "overdue"].includes(loan.loan_status)),
    [loans]
  );

  const selectedLoan = useMemo(
    () => renewableLoans.find((loan) => loan.loan_no === form.loan_no) || null,
    [renewableLoans, form.loan_no]
  );

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const [loansRes, renewalsRes] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getLoans`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getRenewals`, { headers })
      ]);

      setLoans(loansRes.data || []);
      setRenewals(renewalsRes.data || []);
    } catch (fetchError) {
      setError("ไม่สามารถโหลดข้อมูลต่ออายุได้");
      console.error("LibraryRenewals fetch error:", fetchError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedLoan) return;

    const baseDate = new Date(selectedLoan.due_date || new Date());
    if (Number.isNaN(baseDate.getTime())) return;
    baseDate.setDate(baseDate.getDate() + 7);

    setForm((prev) => ({
      ...prev,
      new_due_date: prev.new_due_date || toDateInputValue(baseDate)
    }));
  }, [selectedLoan]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const t_id = getUserId();
    if (!t_id) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!form.loan_no || !form.new_due_date) {
      setError("กรุณาเลือกรายการยืมและกำหนดวันครบกำหนดใหม่");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API}/library/renewLoan`,
        {
          loan_no: form.loan_no,
          new_due_date: form.new_due_date,
          renew_note: form.renew_note.trim() || null,
          t_id
        },
        { headers }
      );

      setSuccess(`ต่ออายุสำเร็จ เลขที่ยืม: ${response.data?.loan_no || "-"}`);
      setForm({ loan_no: "", new_due_date: "", renew_note: "" });
      await fetchData();
    } catch (submitError) {
      const message = submitError?.response?.data?.message;
      if (message === "loan not found") {
        setError("ไม่พบเลขที่รายการยืม");
      } else if (message === "renew limit reached") {
        setError("รายการนี้ต่ออายุครบจำนวนครั้งสูงสุดแล้ว");
      } else if (message === "loan cannot renew") {
        setError("รายการนี้ไม่สามารถต่ออายุได้");
      } else if (message === "new due date must be later") {
        setError("วันครบกำหนดใหม่ต้องมากกว่าวันเดิม");
      } else {
        setError("บันทึกการต่ออายุไม่สำเร็จ");
      }
      console.error("Renew loan error:", submitError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">งานต่ออายุการยืมหนังสือ</h4>
          <Badge bg="primary">Renewal</Badge>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3">
            {success}
          </Alert>
        )}

        <Row className="g-3 mb-3">
          <Col lg={5}>
            <Card>
              <Card.Header className="fw-semibold">บันทึกการต่ออายุ</Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>เลือกรายการยืม</Form.Label>
                    <Form.Select
                      value={form.loan_no}
                      onChange={(event) => setForm((prev) => ({ ...prev, loan_no: event.target.value }))}
                      required
                    >
                      <option value="">-- เลือกรายการ --</option>
                      {renewableLoans.map((loan) => (
                        <option key={loan.loan_no} value={loan.loan_no}>
                          {loan.loan_no} - {loan.copy_code} - {loan.borrower_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {selectedLoan && (
                    <Alert variant="light" className="border">
                      <div><strong>ผู้ยืม:</strong> {selectedLoan.borrower_name} ({selectedLoan.borrower_idnumber})</div>
                      <div><strong>หนังสือ:</strong> {selectedLoan.title_name}</div>
                      <div><strong>ครบกำหนดเดิม:</strong> {toDateInputValue(selectedLoan.due_date) || "-"}</div>
                      <div><strong>ต่ออายุแล้ว:</strong> {selectedLoan.renew_count || 0} ครั้ง</div>
                    </Alert>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>วันครบกำหนดใหม่</Form.Label>
                    <ThaiDateInput
                      value={form.new_due_date}
                      onChange={(event) => setForm((prev) => ({ ...prev, new_due_date: event.target.value }))}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>หมายเหตุ</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={form.renew_note}
                      onChange={(event) => setForm((prev) => ({ ...prev, renew_note: event.target.value }))}
                      placeholder="บันทึกเหตุผลหรือเงื่อนไขเพิ่มเติม"
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button type="submit" disabled={submitting || loading}>
                      {submitting ? "กำลังบันทึก..." : "บันทึกการต่ออายุ"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7}>
            <Card>
              <Card.Header className="fw-semibold">รายการยืมที่ต่ออายุได้</Card.Header>
              <Card.Body className="table-responsive">
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <Table hover size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Loan No</th>
                        <th>ผู้ยืม</th>
                        <th>หนังสือ</th>
                        <th>ครบกำหนด</th>
                        <th>สถานะ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renewableLoans.map((loan) => (
                        <tr
                          key={loan.loan_no}
                          onClick={() => setForm((prev) => ({ ...prev, loan_no: loan.loan_no }))}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{loan.loan_no}</td>
                          <td>{loan.borrower_name}</td>
                          <td>{loan.title_name}</td>
                          <td>{toDateInputValue(loan.due_date)}</td>
                          <td>{statusLabel[loan.loan_status] || loan.loan_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Header className="fw-semibold">ประวัติการต่ออายุล่าสุด</Card.Header>
          <Card.Body className="table-responsive">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <Table hover size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>วันที่</th>
                    <th>Loan No</th>
                    <th>ผู้ยืม</th>
                    <th>หนังสือ</th>
                    <th>ครบกำหนดเดิม</th>
                    <th>ครบกำหนดใหม่</th>
                    <th>ผู้ดำเนินการ</th>
                  </tr>
                </thead>
                <tbody>
                  {renewals.slice(0, 100).map((row) => (
                    <tr key={row.renew_id}>
                      <td>{formatDateTime(row.renew_date)}</td>
                      <td>{row.loan_no}</td>
                      <td>{row.borrower_name}</td>
                      <td>{row.title_name}</td>
                      <td>{toDateInputValue(row.old_due_date)}</td>
                      <td>{toDateInputValue(row.new_due_date)}</td>
                      <td>{row.officer_name || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </main>
      <Footer />
    </React.Fragment>
  );
}
